import { defineMessages } from 'react-intl';

export const messages = defineMessages({
  autosaveNote: {
    id: `app.containers.ScorecardModals.ScorecardForm.autosaveNote`,
    defaultMessage:
      'Your answers save as you go — you can close this link and come back to finish.',
    description:
      'Reassurance on the standalone evaluator scorecard page that answers autosave',
  },
  draftRestored: {
    id: `app.containers.ScorecardModals.ScorecardForm.draftRestored`,
    defaultMessage:
      'We picked up where you left off — your saved answers are below.',
    description:
      'Banner shown when a returning evaluator has autosaved answers',
  },
  scoring: {
    id: `app.containers.ScorecardModals.ScorecardForm.scoring`,
    defaultMessage: 'Scoring',
    description: 'Label for the scorecard scoring type in the header context',
  },
  modeScored: {
    id: `app.containers.ScorecardModals.ScorecardForm.modeScored`,
    defaultMessage: 'Scored',
    description: 'Scoring type shown for an unweighted scorecard',
  },
  modeWeighted: {
    id: `app.containers.ScorecardModals.ScorecardForm.modeWeighted`,
    defaultMessage: 'Weighted',
    description: 'Scoring type shown for a weighted scorecard',
  },
  modeCommentsOnly: {
    id: `app.containers.ScorecardModals.ScorecardForm.modeCommentsOnly`,
    defaultMessage: 'Comments only',
    description: 'Scoring type shown for a scorecard that produces no score',
  },
  weightedTotal: {
    id: `app.containers.ScorecardModals.ScorecardForm.weightedTotal`,
    defaultMessage: 'Weighted total',
    description: 'Label for the running weighted score in the modal footer',
  },
  averageScore: {
    id: `app.containers.ScorecardModals.ScorecardForm.averageScore`,
    defaultMessage: 'Average score',
    description: 'Label for the running average score in the modal footer',
  },
  scoringNote: {
    id: `app.containers.ScorecardModals.ScorecardForm.scoringNote`,
    defaultMessage: 'Only rating scale questions count toward the score.',
    description: 'Note under the scorecard header on scored templates',
  },
  enterNotes: {
    id: `app.containers.ScorecardModals.ScorecardForm.enterNotes`,
    defaultMessage: 'Enter notes…',
    description: 'Placeholder for the per-question notes field',
  },
  selectAnOption: {
    id: `app.containers.ScorecardModals.ScorecardForm.selectAnOption`,
    defaultMessage: 'Select an option…',
    description: 'Placeholder for a dropdown scorecard question',
  },
  questionNeedsAnswer: {
    id: `app.containers.ScorecardModals.ScorecardForm.questionNeedsAnswer`,
    defaultMessage: 'This question needs an answer before you can submit.',
    description: 'Error under an unanswered required scorecard question',
  },
  chooseRecommendation: {
    id: `app.containers.ScorecardModals.ScorecardForm.chooseRecommendation`,
    defaultMessage: 'Choose a recommendation before submitting.',
    description: 'Error under the unanswered recommendation question',
  },
  missingSummary: {
    id: `app.containers.ScorecardModals.ScorecardForm.missingSummary`,
    defaultMessage:
      '{count, plural, one {# question still needs an answer.} other {# questions still need an answer.}}',
    description:
      'Summary error next to the submit button counting unanswered required questions',
  },
  recommendationSummary: {
    id: `app.containers.ScorecardModals.ScorecardForm.recommendationSummary`,
    defaultMessage: 'Choose an overall recommendation before submitting.',
    description:
      'Summary error next to the submit button when only the recommendation is missing',
  },
  draftSaved: {
    id: `app.containers.ScorecardModals.ScorecardForm.draftSaved`,
    defaultMessage: 'Draft saved',
    description: 'Indicator that scorecard answers were autosaved',
  },
  saveFailed: {
    id: `app.containers.ScorecardModals.ScorecardForm.saveFailed`,
    defaultMessage:
      "We couldn't save your answers. Check your connection and keep going — we'll retry on your next change.",
    description: 'Error shown when a scorecard autosave request fails',
  },
  loadFailed: {
    id: `app.containers.ScorecardModals.ScorecardForm.loadFailed`,
    defaultMessage:
      "We couldn't load the scorecard. Refresh the page or try again later.",
    description: 'Error shown when the scorecard context fails to load',
  },
  submitFailed: {
    id: `app.containers.ScorecardModals.ScorecardForm.submitFailed`,
    defaultMessage: "We couldn't submit your scorecard. Try again.",
    description: 'Error shown when the scorecard submission request fails',
  },
  recommendationHeading: {
    id: `app.containers.ScorecardModals.ScorecardForm.recommendationHeading`,
    defaultMessage: 'Recommendation for hire',
    description: 'Heading over the overall recommendation options',
  },
  recommendationStrongNo: {
    id: `app.containers.ScorecardModals.ScorecardForm.recommendationStrongNo`,
    defaultMessage: 'Strong no',
    description: 'Recommendation option label for the strong_no key',
  },
  recommendationStrongYes: {
    id: `app.containers.ScorecardModals.ScorecardForm.recommendationStrongYes`,
    defaultMessage: 'Strong yes',
    description: 'Recommendation option label for the strong_yes key',
  },
  submitScorecard: {
    id: `app.containers.ScorecardModals.ScorecardForm.submitScorecard`,
    defaultMessage: 'Submit scorecard',
    description: 'Submit button on the scorecard fill form',
  },
  confirmTitle: {
    id: `app.containers.ScorecardModals.ScorecardForm.confirmTitle`,
    defaultMessage: 'Submit scorecard?',
    description: 'Title of the scorecard submission confirmation dialog',
  },
  confirmBody: {
    id: `app.containers.ScorecardModals.ScorecardForm.confirmBody`,
    defaultMessage:
      "Once submitted, your scorecard can't be edited. Your scores and notes will be visible to anyone with access to this opening.",
    description: 'Body of the scorecard submission confirmation dialog',
  },
  submittedTitle: {
    id: `app.containers.ScorecardModals.ScorecardForm.submittedTitle`,
    defaultMessage: 'Scorecard submitted',
    description: 'Title of the post-submission state on the evaluator page',
  },
  submittedBody: {
    id: `app.containers.ScorecardModals.ScorecardForm.submittedBody`,
    defaultMessage:
      "Your {scorecardName} for {applicantName} is in. It can't be edited, and you no longer have a scorecard to open for this applicant — you'll hear from the recruiting team if they need anything else.",
    description: 'Body of the post-submission state on the evaluator page',
  },
  submittedClose: {
    id: `app.containers.ScorecardModals.ScorecardForm.submittedClose`,
    defaultMessage: 'You can close this page.',
    description: 'Hint under the post-submission state on the evaluator page',
  },
});
